/**
 * spaceItemsModel — pure shape + bucketing logic for the private life-space item
 * collections (personalItems / ownersItems / barbaraItems / captureItems).
 *
 * TWO SHAPES ON PURPOSE, and this is the load-bearing bit.
 * These collections were populated by `scripts/migration/importPrivateSpaces.js`,
 * which carried the Notion field names across verbatim: `name` for the label and
 * `status: 'open'` for the state. The rest of this repo says `title` and uses the
 * lowercase `pending | in_progress | completed` vocabulary that `teacherTasks`
 * uses. Rather than migrate 18 documents to settle a naming difference — which
 * would quietly commit the Personal space to the Notion shape before the actual
 * Tasks-surface decision has been made — the READ layer tolerates both and
 * normalizes to the repo vocabulary. Nothing is rewritten, nothing is lost, and
 * whichever shape wins later needs no undo here.
 *
 * UNRECOGNIZED STATUS FAILS OPEN. normalizeStatus() returns 'pending' for
 * anything it does not recognize, so a typo or a future status value surfaces the
 * item rather than hiding it. For a personal to-do list the harm of showing a
 * stale row is a moment's confusion; the harm of hiding a live one is missing it
 * entirely, and the second failure is silent.
 */

export const ITEM_STATUS = {
	PENDING: 'pending',
	IN_PROGRESS: 'in_progress',
	COMPLETED: 'completed',
};

/** How far ahead "This week" reaches. Overdue items are always included. */
export const DUE_SOON_DAYS = 7;

/** How many rows a slot shows before collapsing the rest into "+N more". */
export const SLOT_PREVIEW_MAX = 5;

/**
 * Firestore Timestamp | Date | {_seconds} | null -> Date | null.
 * The admin SDK and the web SDK hand back different representations of the same
 * field, and fixtures hand back plain Dates, so all three are accepted.
 */
export function toDate(value) {
	if (!value) return null;
	if (typeof value.toDate === 'function') return value.toDate();
	if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;
	if (typeof value._seconds === 'number') return new Date(value._seconds * 1000);
	return null;
}

/** Notion's 'open' and the repo's own vocabulary both land on a repo status. */
export function normalizeStatus(raw) {
	const v = String(raw ?? '').toLowerCase().trim();
	if (v === 'completed' || v === 'complete' || v === 'done') {
		return ITEM_STATUS.COMPLETED;
	}
	if (v === 'in_progress' || v === 'in progress' || v === 'doing') {
		return ITEM_STATUS.IN_PROGRESS;
	}
	// 'open' (Notion import), '' (captureItems), and anything unknown.
	return ITEM_STATUS.PENDING;
}

/**
 * Raw Firestore data -> the shape every Personal surface reads.
 * `captureItems` has no `status` field at all — it tracks completion with the
 * boolean `doneWatched`, so that is folded in here rather than at each call site.
 */
export function normalizeItem(raw = {}, id = null) {
	const status =
		raw.doneWatched === true
			? ITEM_STATUS.COMPLETED
			: normalizeStatus(raw.status);

	return {
		id,
		title: raw.title ?? raw.name ?? '',
		type: raw.type ?? 'task',
		status,
		dueAt: toDate(raw.dueAt),
		amount: raw.amount ?? null,
		recurring: raw.recurring === true,
		link: raw.link ?? null,
		notes: raw.notes ?? '',
	};
}

export function isOpenItem(item) {
	return item?.status !== ITEM_STATUS.COMPLETED;
}

/**
 * A stored due date is a CALENDAR DATE, not an instant, and the two must not be
 * read with the same getters.
 *
 * The Notion import wrote every `dueAt` as midnight UTC — "September 1st" is
 * stored as 2026-09-01T00:00:00Z. Read with local getters west of Greenwich that
 * is August 31st at 19:00, so a plain `.getDate()` renders the real Barbara
 * deadline "Sort out safe deposit box in Yonkers before September 1st" as
 * **Aug 31**. Every date-only value in these collections is off by one day, in
 * the direction that makes a deadline look nearer than it is — silently, since
 * nothing on screen shows the timestamp it came from.
 *
 * So: the stored value's intended calendar date is its UTC year/month/day, and
 * it is re-anchored to LOCAL midnight for comparison against the user's own
 * "today". Mixing them deliberately — the date means what it said in Notion, and
 * "today" means today where the reader is standing.
 */
function toCalendarDate(value) {
	const d = toDate(value);
	if (!d) return null;
	return new Date(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate());
}

function startOfDay(d) {
	return new Date(d.getFullYear(), d.getMonth(), d.getDate());
}

/** Whole days from the reader's today to a calendar date; negative = overdue. */
function daysUntil(dueAt, now) {
	const due = toCalendarDate(dueAt);
	if (!due) return null;
	return Math.round((due - startOfDay(now)) / 86400000);
}

/** Overdue or falling inside the next DUE_SOON_DAYS days. */
export function isDueSoon(item, now = new Date()) {
	const days = daysUntil(item?.dueAt, now);
	return days !== null && days <= DUE_SOON_DAYS;
}

/** Due-dated first (soonest first), then undated, alphabetical within each. */
export function sortItems(items) {
	return [...items].sort((a, b) => {
		if (a.dueAt && b.dueAt) {
			const diff = a.dueAt - b.dueAt;
			if (diff !== 0) return diff;
		} else if (a.dueAt) {
			return -1;
		} else if (b.dueAt) {
			return 1;
		}
		return String(a.title).localeCompare(String(b.title));
	});
}

const WEEKDAY = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MONTH = [
	'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
	'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
];

/** Short, scannable due chip: Overdue / Today / Tomorrow / Fri / Sep 1. */
export function formatDueLabel(dueAt, now = new Date()) {
	const days = daysUntil(dueAt, now);
	if (days === null) return '';
	if (days < 0) return 'Overdue';
	if (days === 0) return 'Today';
	if (days === 1) return 'Tomorrow';
	// Re-anchored to local midnight above, so these getters read the date the
	// value was written to mean rather than the local shift of its UTC instant.
	const cal = toCalendarDate(dueAt);
	if (days < DUE_SOON_DAYS) return WEEKDAY[cal.getDay()];
	return `${MONTH[cal.getMonth()]} ${cal.getDate()}`;
}

export function isOverdue(item, now = new Date()) {
	const days = daysUntil(item?.dueAt, now);
	return days !== null && days < 0;
}

/**
 * The Capture inbox slot.
 *
 * WHAT HAPPENED TO THE OTHER THREE SLOTS. Personal's tasks moved to the real task
 * machinery (`personalTasks`, the Work module bound through TaskServiceContext),
 * so the This week / Bills due / Everything else buckets over `personalItems`
 * retired with the mount — Anthony chose to start the new surface empty rather
 * than migrate the ten legacy documents. Those documents still exist in
 * Firestore; nothing reads them.
 *
 * CAPTURE DID NOT RETIRE WITH THEM, and that is deliberate rather than an
 * oversight left behind. It is not a task inbox: it is a shared read-later
 * library for Anthony and Grace — links caught from a phone during the day, and
 * the things they send each other in the morning. Folding it into the task list
 * would wreck both halves, because a to-do list that fills with "watch this
 * sometime" stops being trusted, and a capture inbox that makes you decide
 * whether something is a task stops being used. It keeps its own collection,
 * its own slot, and its own future (rich link cards, reader mode, an iOS
 * Shortcut posting straight into it).
 */
export function buildCaptureSlots(captures = []) {
	return [
		{
			key: 'capture',
			name: 'Capture inbox',
			icon: 'fa-inbox',
			items: sortItems(captures.filter(isOpenItem)),
			hint: 'Inbox is empty',
			sub: 'Links and notes you save to come back to later.',
			cta: 'Capture',
			ctaIcon: 'fa-bolt',
		},
	];
}

/**
 * The one-line meta the space switcher shows under a space name. Returns '' to
 * mean "use the space's static zero copy".
 */
export function summarizeCaptures(captures = []) {
	const open = captures.filter(isOpenItem).length;
	return open === 0 ? '' : `${open} saved`;
}
